import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BarChart3, ExternalLink } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, formatDate, formatNumber } from '../../../components/design-system/admin';
import { BarList, DistributionBar, MiniStat } from '../../../components/design-system/analytics';
import { useUnitEconomicsApi } from '../../../common/hooks/use-unit-economics-api.hook';
import type { EconomicsRow, VendorEconomicsDetail } from '../../../common/models/unit-economics.model';
import { money, moneyShort } from '../costs/cost-format';
import { billingLine, listLine } from './CustomersTable';
import { FlagBadges } from './FlagBadges';
import { HistoryChart } from './HistoryChart';
import { costParts, formatMultiple, monthLabel } from './ue-format';

function Section({ title, hint, children, testId }: { title: ReactNode; hint?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section className="flex flex-col gap-3 border-t px-5 py-4" data-testid={testId}>
      <div>
        <h3 className="text-sm font-medium">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Line({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-end tabular-nums">{children}</dd>
    </div>
  );
}

/**
 * One customer's economics: the month (what they pay, cost, margin), six
 * months of revenue against cost, where the cost went by feature and by
 * ledger, the sales we influenced for them, and what they used.
 */
export function VendorEconomicsSheet({ row, month, onClose }: { row: EconomicsRow | null; month: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const api = useUnitEconomicsApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [detail, setDetail] = useState<VendorEconomicsDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const lang = (i18n.language || 'en').slice(0, 2);
  const vendorId = row?.vendorId;

  useEffect(() => {
    if (!vendorId) return;
    let live = true;
    setDetail(null);
    setFailed(false);
    apiRef.current
      .vendor(vendorId, month)
      .then((d) => live && setDetail(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [vendorId, month]);

  const r = detail?.row || row;
  const feature = (k: string) => t(`UNIT_ECONOMICS.FEATURES.${k}`);

  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="gap-0 data-[side=right]:sm:max-w-xl" data-testid="ue-drawer">
        <SheetHeader className="pe-12">
          <SheetTitle className="text-base">{r?.name}</SheetTitle>
          <SheetDescription>{r ? `${billingLine(r, t)} · ${monthLabel(month, lang)}` : ''}</SheetDescription>
          {r && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <FlagBadges flags={r.flags} assumptions={detail?.assumptions} />
              <div className="ms-auto flex gap-1.5">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/vendors/${r.vendorId}/edit`}>
                    <ExternalLink />
                    {t('UNIT_ECONOMICS.DRAWER.OPEN_VENDOR')}
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/vendors/${r.vendorId}/edit?tab=usage`}>
                    <BarChart3 />
                    {t('UNIT_ECONOMICS.DRAWER.OPEN_USAGE')}
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1">
          {r && (
            <div className="grid grid-cols-2 gap-4 px-5 pb-4 sm:grid-cols-4" data-testid="ue-drawer-stats">
              <MiniStat label={t('UNIT_ECONOMICS.TABLE.MRR')} value={r.billing.mrrUsd == null ? '—' : moneyShort(r.billing.mrrUsd)} sub={listLine(r, t) || undefined} />
              <MiniStat label={t('UNIT_ECONOMICS.DRAWER.REVENUE')} value={r.revenueUsd == null ? '—' : moneyShort(r.revenueUsd)} sub={detail?.current ? t('UNIT_ECONOMICS.DRAWER.SO_FAR') : undefined} />
              <MiniStat label={t('UNIT_ECONOMICS.DRAWER.COST')} value={money(r.costs.totalUsd)} />
              <MiniStat
                label={t('UNIT_ECONOMICS.DRAWER.MARGIN')}
                value={<span className={cn(r.marginUsd != null && r.marginUsd < 0 && r.billing.status === 'paying' && 'text-destructive')}>{r.marginUsd == null ? '—' : money(r.marginUsd)}</span>}
                sub={r.marginPct == null ? undefined : `${formatNumber(r.marginPct, 1)}%`}
              />
            </div>
          )}

          {failed ? (
            <EmptyState title={t('UNIT_ECONOMICS.DRAWER.LOAD_FAILED')} className="py-16" />
          ) : !detail ? (
            <div className="flex flex-col gap-3 border-t px-5 py-4">
              <Skeleton className="h-52 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : (
            <>
              <Section title={t('UNIT_ECONOMICS.DRAWER.HISTORY')} hint={t('UNIT_ECONOMICS.DRAWER.HISTORY_HINT')} testId="ue-drawer-history">
                <HistoryChart history={detail.history} />
              </Section>

              <Section title={t('UNIT_ECONOMICS.DRAWER.BILLING')} testId="ue-drawer-billing">
                <dl className="flex flex-col gap-1.5">
                  <Line label={t('UNIT_ECONOMICS.DRAWER.PLAN')}>{detail.row.plan.name || '—'}</Line>
                  <Line label={t('UNIT_ECONOMICS.DRAWER.SOURCE')}>
                    {t(`UNIT_ECONOMICS.SOURCE.${detail.row.billing.source}`)}
                    {detail.row.billing.monthly != null && detail.row.billing.currency && detail.row.billing.currency !== 'usd'
                      ? ` · ${formatNumber(detail.row.billing.monthly, 2)} ${detail.row.billing.currency.toUpperCase()}`
                      : ''}
                  </Line>
                  {detail.row.billing.contract && (
                    <Line label={t('UNIT_ECONOMICS.DRAWER.CONTRACT')}>
                      {detail.row.billing.contract.endDate
                        ? t('UNIT_ECONOMICS.DRAWER.CONTRACT_VALUE', {
                            amount: `${formatNumber(detail.row.billing.contract.monthlyAmount)} ${detail.row.billing.contract.currency.toUpperCase()}`,
                            term: detail.row.billing.contract.termMonths,
                            end: formatDate(detail.row.billing.contract.endDate),
                          })
                        : t('UNIT_ECONOMICS.DRAWER.CONTRACT_OPEN', {
                            amount: `${formatNumber(detail.row.billing.contract.monthlyAmount)} ${detail.row.billing.contract.currency.toUpperCase()}`,
                            term: detail.row.billing.contract.termMonths,
                          })}
                    </Line>
                  )}
                  {detail.row.billing.offer && (
                    <Line label={t('UNIT_ECONOMICS.DRAWER.OFFER')}>
                      {t('UNIT_ECONOMICS.DRAWER.OFFER_VALUE', { amount: `${formatNumber(detail.row.billing.offer.monthly)} ${detail.row.billing.offer.currency.toUpperCase()}` })}
                    </Line>
                  )}
                  {detail.row.list.monthlyUsd != null && (
                    <Line label={t('UNIT_ECONOMICS.DRAWER.LIST')}>{t('UNIT_ECONOMICS.DRAWER.LIST_VALUE', { amount: money(detail.row.list.monthlyUsd) })}</Line>
                  )}
                  {detail.row.billing.trialEndsAt && <Line label={t('UNIT_ECONOMICS.DRAWER.TRIAL_ENDS')}>{formatDate(detail.row.billing.trialEndsAt)}</Line>}
                  {detail.row.renewalAt && <Line label={t('UNIT_ECONOMICS.DRAWER.RENEWAL')}>{formatDate(detail.row.renewalAt)}</Line>}
                  {detail.row.billedUsd != null && <Line label={t('UNIT_ECONOMICS.DRAWER.BILLED')}>{money(detail.row.billedUsd)}</Line>}
                </dl>
              </Section>

              <Section title={t('UNIT_ECONOMICS.DRAWER.COSTS')} testId="ue-drawer-costs">
                {detail.row.costs.totalUsd <= 0 ? (
                  <p className="text-sm text-muted-foreground">{t('UNIT_ECONOMICS.DRAWER.NOTHING')}</p>
                ) : (
                  <>
                    <DistributionBar
                      segments={costParts(detail.row.costs).map((p) => ({ key: p.key, label: t(`UNIT_ECONOMICS.COST_PARTS.${p.key}`), value: p.value }))}
                      format={(v) => money(v)}
                      columns={3}
                    />
                    <h4 className="mt-1 text-xs font-medium text-muted-foreground">{t('UNIT_ECONOMICS.DRAWER.BY_FEATURE')}</h4>
                    <BarList items={detail.breakdown.byFeature.map((f) => ({ key: f.feature, label: feature(f.feature), value: f.totalUsd }))} format={(v) => money(v)} />
                    {!!detail.breakdown.ai.length && (
                      <>
                        <h4 className="mt-1 text-xs font-medium text-muted-foreground">{t('UNIT_ECONOMICS.DRAWER.AI_CALLS')}</h4>
                        <BarList
                          items={detail.breakdown.ai.slice(0, 8).map((a) => ({
                            key: a.feature,
                            label: <span className="font-mono text-xs">{a.feature}</span>,
                            value: a.costUsd,
                            secondary: t('UNIT_ECONOMICS.DRAWER.REQUESTS', { count: a.requests }),
                          }))}
                          format={(v) => money(v)}
                        />
                      </>
                    )}
                    {!!detail.breakdown.generations.length && (
                      <>
                        <h4 className="mt-1 text-xs font-medium text-muted-foreground">{t('UNIT_ECONOMICS.DRAWER.GENERATIONS')}</h4>
                        <BarList
                          items={detail.breakdown.generations.map((g) => ({
                            key: g.recipe,
                            label: <span className="font-mono text-xs">{g.recipe}</span>,
                            value: g.costUsd,
                            secondary: [t('UNIT_ECONOMICS.DRAWER.JOBS', { count: g.jobs }), g.estimatedJobs ? t('UNIT_ECONOMICS.DRAWER.ESTIMATED', { count: g.estimatedJobs }) : null].filter(Boolean).join(' · '),
                          }))}
                          format={(v) => money(v)}
                        />
                      </>
                    )}
                    {!!detail.breakdown.seo.length && (
                      <>
                        <h4 className="mt-1 text-xs font-medium text-muted-foreground">{t('UNIT_ECONOMICS.DRAWER.MARKET')}</h4>
                        <BarList
                          items={detail.breakdown.seo.map((s) => ({ key: s.kind, label: <span className="font-mono text-xs">{s.kind}</span>, value: s.costUsd, secondary: t('UNIT_ECONOMICS.DRAWER.READINGS', { count: s.readings }) }))}
                          format={(v) => money(v)}
                        />
                      </>
                    )}
                    {detail.breakdown.gpu.some((g) => g.jobs > 0) && (
                      <>
                        <h4 className="mt-1 text-xs font-medium text-muted-foreground">{t('UNIT_ECONOMICS.DRAWER.GPU_JOBS')}</h4>
                        <BarList
                          items={detail.breakdown.gpu
                            .filter((g) => g.jobs > 0)
                            .map((g) => ({ key: g.kind, label: t(`UNIT_ECONOMICS.GPU_KINDS.${g.kind}`), value: g.shareUsd, secondary: t('UNIT_ECONOMICS.DRAWER.JOBS', { count: g.jobs }) }))}
                          format={(v) => money(v)}
                        />
                      </>
                    )}
                  </>
                )}
              </Section>

              <Section title={t('UNIT_ECONOMICS.DRAWER.VALUE')} testId="ue-drawer-value">
                {detail.row.value.influencedUsd == null ? (
                  <p className="text-sm text-muted-foreground">{t('UNIT_ECONOMICS.DRAWER.NO_SHOP')}</p>
                ) : (
                  <dl className="flex flex-col gap-1.5">
                    <Line label={t('UNIT_ECONOMICS.DRAWER.INFLUENCED')}>
                      {money(detail.row.value.influencedUsd)}
                      <span className="ms-2 text-xs text-muted-foreground">{t('UNIT_ECONOMICS.DRAWER.INFLUENCED_ORDERS', { count: detail.row.value.influencedOrders })}</span>
                    </Line>
                    <Line label={t('UNIT_ECONOMICS.DRAWER.ROI')}>
                      <span className="font-medium">{formatMultiple(detail.row.value.roi)}</span>
                    </Line>
                    <Line label={t('UNIT_ECONOMICS.DRAWER.SHOP_ORDERS')}>
                      {t('UNIT_ECONOMICS.DRAWER.SHOP_ORDERS_VALUE', { count: detail.row.value.orders, amount: moneyShort(detail.row.value.shopRevenueUsd) })}
                    </Line>
                  </dl>
                )}
              </Section>

              <Section title={t('UNIT_ECONOMICS.DRAWER.USAGE')} testId="ue-drawer-usage">
                <BarList
                  items={detail.usage.map((u) => ({ key: u.feature, label: feature(u.feature), value: u.uses, secondary: u.lastUseAt ? formatDate(u.lastUseAt) : undefined }))}
                  format={(v) => formatNumber(v)}
                  emptyText={t('UNIT_ECONOMICS.DRAWER.USAGE_EMPTY')}
                />
              </Section>
            </>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
